import React from 'react';
import { MapPin, Hammer, Users, ShieldCheck } from 'lucide-react';
import { TESTIDS } from '../constants/testIds';

const POINTS = [
  {
    Icon: MapPin,
    title: 'Local to Leamington',
    body: 'I live in Leamington Spa and work across Warwick, Kenilworth, Whitnash and the surrounding villages — I\u2019m never far from your door.',
  },
  {
    Icon: Hammer,
    title: 'Years of experience',
    body: 'I\u2019ve spent several years specialising in guttering, fascias and soffits — there isn\u2019t a roofline problem I haven\u2019t seen before.',
  },
  {
    Icon: Users,
    title: 'Independent & personal',
    body: 'I\u2019m a genuinely independent local company. You\u2019ll deal directly with me from quote to completion — no call centres, no middlemen.',
  },
  {
    Icon: ShieldCheck,
    title: 'Highest standard',
    body: 'I complete every job to the highest standard, with the same care I\u2019d give my own home.',
  },
];

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-20 sm:py-28 bg-[#0A0A0A] overflow-hidden"
    >
      {/* decorative gold corner */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-[#E8B842] opacity-[0.06] rotate-45 -translate-y-1/2 translate-x-1/2 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Photo — circular portrait */}
          <div className="lg:col-span-4 flex justify-center lg:justify-start lg:sticky lg:top-28">
            <div className="relative w-56 sm:w-64 lg:w-72">
              <div className="relative aspect-square rounded-full overflow-hidden bg-[#121212] border-4 border-[#E8B842] shadow-2xl">
                <img
                  src="/about/luke.jpg"
                  alt="Luke, founder of Leamington Gutters"
                  loading="lazy"
                  data-testid="about-luke-photo"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 25%' }}
                />
              </div>
              <div className="mt-5 text-center lg:text-left">
                <div className="font-display uppercase font-extrabold text-white text-2xl leading-none">
                  Luke
                </div>
                <div className="font-display uppercase tracking-widest text-xs text-[#E8B842] mt-1">
                  Founder &middot; Leamington Spa
                </div>
              </div>
            </div>
          </div>

          {/* Copy */}
          <div className="lg:col-span-8">
            <span className="font-display uppercase tracking-[0.4em] text-xs text-[#E8B842]">
              / About us
            </span>
            <div className="mt-6 space-y-4 text-[#C7C7C7] text-base sm:text-lg leading-relaxed max-w-2xl">
              <p>
                Leamington Gutters is my <strong className="text-white">independent local company</strong>.
                I live in Leamington Spa and have spent several years working hands-on with
                guttering, fascias and soffits across Warwickshire &mdash; so you can trust me
                to spot problems early and fix them properly the first time.
              </p>
              <p>
                Being independent means I can offer you a{' '}
                <strong className="text-white">personalised service tailored to your needs</strong>
                {' '}&mdash; honest advice, a fair price and the same friendly face from quote
                to completion. I complete every job to the highest standard and I&rsquo;m happy
                to talk you through exactly what I&rsquo;m doing and why.
              </p>
            </div>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {POINTS.map(({ Icon, title, body }) => (
                <div
                  key={title}
                  className="border border-[#E8B842]/15 bg-[#0E0E0E] p-5 hover:border-[#E8B842]/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#E8B842]/10 border border-[#E8B842]/30 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-[#E8B842]" />
                    </div>
                    <h3 className="font-display uppercase tracking-wider text-white text-base font-bold">
                      {title}
                    </h3>
                  </div>
                  <p className="text-sm text-[#A3A3A3] leading-relaxed mt-3">{body}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href="#contact"
                data-testid="about-cta-quote"
                className="inline-flex items-center justify-center gap-2 bg-[#E8B842] hover:bg-[#F4C534] text-black font-display font-bold uppercase tracking-wider px-7 py-3.5 transition-colors"
              >
                Get a Free Quote
              </a>
              <a
                href="#gallery"
                className="inline-flex items-center justify-center gap-2 border border-[#E8B842]/40 text-[#E8B842] hover:bg-[#E8B842]/10 font-display font-bold uppercase tracking-wider px-7 py-3.5 transition-colors"
              >
                See our work
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
